import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { MessageCircle, Database, Megaphone, Loader2 } from "lucide-react";
import { MetaAdsLauncher } from "@/components/MetaAdsLauncher";

const SEGS = { student: "Students", employer: "Employers", expert: "Experts" };

const Status = ({ on }) => (
  <Badge className={on ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"} data-testid="integration-status">{on ? "Connected" : "Not connected"}</Badge>
);

const Head = ({ icon: Icon, title, on, children }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="flex items-center gap-2"><Icon className="h-5 w-5 text-amber-600" /><p className="font-heading text-lg font-bold">{title}</p></div>
    <div className="flex items-center gap-3">{children}<Status on={on} /></div>
  </div>
);

export const IntegrationsPanel = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/integrations").then(({ data: d }) => setData(d)).catch(() => toast.error("Could not load integrations"));
  }, []);
  if (!data) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-amber-600" />;
  const s = data.settings;
  const set = (patch) => setData({ ...data, settings: { ...s, ...patch } });
  const save = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.put("/admin/integrations", s);
      setData(d);
      toast.success("Integration settings saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-5" data-testid="integrations-panel">
      <Card className="border-slate-200 p-5" data-testid="integration-aisensy">
        <Head icon={MessageCircle} title="AiSensy WhatsApp" on={data.connected.aisensy}>
          <Switch checked={s.aisensy_enabled} onCheckedChange={(v) => set({ aisensy_enabled: v })} data-testid="aisensy-enabled" />
        </Head>
        <p className="mt-2 text-sm text-slate-500">Each new funnel lead gets your approved WhatsApp template instantly. In AiSensy, create a live <b>API Campaign</b> per audience (template with one variable for the first name) and type its exact name here. Replies and conversations are handled by your AiSensy chatbot flow.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {Object.entries(SEGS).map(([k, label]) => (
            <div key={k}>
              <Label htmlFor={`camp-${k}`}>{label} campaign name</Label>
              <Input id={`camp-${k}`} value={s.aisensy_campaigns[k] || ""} onChange={(e) => set({ aisensy_campaigns: { ...s.aisensy_campaigns, [k]: e.target.value } })} placeholder={`c2c_${k}_welcome`} className="mt-1" data-testid={`aisensy-campaign-${k}`} />
            </div>
          ))}
        </div>
      </Card>
      <Card className="border-slate-200 p-5" data-testid="integration-corefactors">
        <Head icon={Database} title="Corefactors CRM" on={data.connected.corefactors}>
          <Switch checked={s.corefactors_enabled} onCheckedChange={(v) => set({ corefactors_enabled: v })} data-testid="corefactors-enabled" />
        </Head>
        <p className="mt-2 text-sm text-slate-500">Every funnel lead (including Meta instant-form leads) is sent to your Corefactors Lead Box with name, email, mobile, city, company, segment, lead quality, score, source and quiz answers. In Corefactors Lead API configuration, add fields with these names: first_name, email, mobile, city, company, source, segment, lead_quality, score, message.</p>
      </Card>
      <Button onClick={save} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="integrations-save">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Save settings</Button>
      <Card className="border-slate-200 p-5" data-testid="integration-meta">
        <Head icon={Megaphone} title="Meta Ads (Facebook & Instagram)" on={data.connected.meta} />
        {data.connected.meta ? <MetaAdsLauncher /> : <p className="mt-2 text-sm text-slate-500">Connect your Meta ad account to create ads here. They are always created paused so you can review them in Ads Manager.</p>}
      </Card>
    </div>
  );
};
